import { useCallback, useState } from 'react';
import {
  eachDayOfInterval,
  format,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { getBusinessLeadCreatedAtRanges } from '../lib/crm/leadCreatedAtRanges';

export type LeadStatus = 'Open' | 'Won' | 'Lost';

export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
  Open: '#2563eb',
  Won: '#10b981',
  Lost: '#ef4444',
};

export interface LeadsDayRow {
  date: string;
  label: string;
  Open: number;
  Won: number;
  Lost: number;
  total: number;
  isToday: boolean;
}

export interface LeadsOutcomeRow {
  key: LeadStatus;
  label: string;
  count: number;
  value: number;
  /** 0..1 share of the month's leads. */
  share: number;
}

export interface LeadsFeedRow {
  id: number;
  title: string;
  subtitle: string;
  value: number;
  status: LeadStatus;
}

export interface LeadsFeedDay {
  date: string;
  label: string;
  isToday: boolean;
  rows: LeadsFeedRow[];
}

export interface LeadsMonthSummary {
  totalLeads: number;
  pipelineValue: number;
  leadsToday: number;
  avgPerDay: number;
  projectedMonthEnd: number;
  /** Cohort outcomes: current status of leads created this month. */
  openCount: number;
  wonCount: number;
  lostCount: number;
  wonValue: number;
  /** Won ÷ created this month — same definition as the metrics hook's conversion rate. */
  conversionRate: number;
}

const EMPTY_SUMMARY: LeadsMonthSummary = {
  totalLeads: 0,
  pipelineValue: 0,
  leadsToday: 0,
  avgPerDay: 0,
  projectedMonthEnd: 0,
  openCount: 0,
  wonCount: 0,
  lostCount: 0,
  wonValue: 0,
  conversionRate: 0,
};

type LeadRow = {
  id: number;
  title: string | null;
  value: number | null;
  source: string | null;
  status: string | null;
  created_at: string;
  business_name: string | null;
  customer_id: string | null;
};

function normalizeStatus(status: string | null): LeadStatus {
  if (status === 'Won') return 'Won';
  if (status === 'Lost') return 'Lost';
  return 'Open';
}

/**
 * This month's lead cohort for the New leads modal: daily volume split by
 * current outcome, cohort outcome totals, and a day-grouped feed.
 *
 * Scope is deliberately narrow — source/lost-reason breakdowns come from the
 * existing `useCrmLeadsBySourcePie`, and headline/health KPIs come from
 * `useCrmLeadMetrics` (passed into the modal), so nothing is queried twice.
 */
export function useCrmLeadsMonthDetails() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const [summary, setSummary] = useState<LeadsMonthSummary>(EMPTY_SUMMARY);
  const [days, setDays] = useState<LeadsDayRow[]>([]);
  const [outcomes, setOutcomes] = useState<LeadsOutcomeRow[]>([]);
  const [feedDays, setFeedDays] = useState<LeadsFeedDay[]>([]);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setOutcomes([]);
      setFeedDays([]);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { monthStart, nextMonthStart } = getBusinessLeadCreatedAtRanges(timezone);
      const zonedNow = toZonedTime(new Date(), timezone);
      setMonthLabel(format(zonedNow, 'MMMM yyyy'));

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('id, title, value, source, status, created_at, business_name, customer_id')
        .eq('business_id', businessId)
        .not('created_at', 'is', null)
        .gte('created_at', monthStart)
        .lt('created_at', nextMonthStart);

      if (queryError) throw queryError;
      const rows = (data ?? []) as LeadRow[];

      // Customer names in one follow-up query (customer_id is legacy text on
      // this table, so an embedded join isn't reliable). Best-effort.
      const customerIds = [
        ...new Set(rows.map((r) => r.customer_id).filter((id): id is string => !!id)),
      ];
      const nameById = new Map<string, string>();
      if (customerIds.length > 0) {
        const { data: customers, error: customerError } = await supabase
          .from('customers')
          .select('id, first_name, last_name')
          .in('id', customerIds);
        if (customerError) {
          console.warn('Could not load lead customer names:', customerError);
        } else {
          for (const c of customers ?? []) {
            const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
            if (name) nameById.set(c.id, name);
          }
        }
      }

      const todayKey = format(zonedNow, 'yyyy-MM-dd');
      const byDay = new Map<string, { Open: number; Won: number; Lost: number }>();
      const counts: Record<LeadStatus, number> = { Open: 0, Won: 0, Lost: 0 };
      const values: Record<LeadStatus, number> = { Open: 0, Won: 0, Lost: 0 };
      let pipelineValue = 0;
      const feedEntries: { dayKey: string; row: LeadsFeedRow }[] = [];

      for (const row of rows) {
        const status = normalizeStatus(row.status);
        const value = Number(row.value) || 0;
        counts[status] += 1;
        values[status] += value;
        pipelineValue += value;

        // Day keys use the business timezone so they line up with the month range.
        const dayKey = format(toZonedTime(new Date(row.created_at), timezone), 'yyyy-MM-dd');
        const day = byDay.get(dayKey) ?? { Open: 0, Won: 0, Lost: 0 };
        day[status] += 1;
        byDay.set(dayKey, day);

        const customerName = row.customer_id ? nameById.get(row.customer_id) : undefined;
        const subtitleParts = [
          customerName || row.business_name?.trim() || null,
          row.source?.trim() || null,
        ].filter((part): part is string => !!part);

        feedEntries.push({
          dayKey,
          row: {
            id: row.id,
            title: row.title?.trim() || 'Untitled lead',
            subtitle: subtitleParts.join(' · ') || 'No source recorded',
            value,
            status,
          },
        });
      }

      const totalLeads = rows.length;
      const monthStartDate = startOfMonth(zonedNow);
      const dayRows: LeadsDayRow[] = eachDayOfInterval({
        start: startOfDay(monthStartDate),
        end: startOfDay(zonedNow),
      }).map((day) => {
        const key = format(day, 'yyyy-MM-dd');
        const sums = byDay.get(key) ?? { Open: 0, Won: 0, Lost: 0 };
        return {
          date: key,
          label: format(day, 'MMM d'),
          Open: sums.Open,
          Won: sums.Won,
          Lost: sums.Lost,
          total: sums.Open + sums.Won + sums.Lost,
          isToday: key === todayKey,
        };
      });

      const elapsedDays = dayRows.length || 1;
      const avgPerDay = totalLeads / elapsedDays;
      const outcomeRows: LeadsOutcomeRow[] = (['Open', 'Won', 'Lost'] as LeadStatus[])
        .map((status) => ({
          key: status,
          label:
            status === 'Open' ? 'Still open' : status === 'Won' ? 'Won' : 'Lost',
          count: counts[status],
          value: values[status],
          share: totalLeads > 0 ? counts[status] / totalLeads : 0,
        }))
        .filter((row) => row.count > 0);

      feedEntries.sort(
        (a, b) => b.dayKey.localeCompare(a.dayKey) || b.row.value - a.row.value,
      );
      const feedByDay = new Map<string, LeadsFeedDay>();
      for (const entry of feedEntries) {
        let day = feedByDay.get(entry.dayKey);
        if (!day) {
          const date = new Date(`${entry.dayKey}T00:00:00`);
          day = {
            date: entry.dayKey,
            label: format(date, 'EEE, MMM d'),
            isToday: entry.dayKey === todayKey,
            rows: [],
          };
          feedByDay.set(entry.dayKey, day);
        }
        day.rows.push(entry.row);
      }

      setSummary({
        totalLeads,
        pipelineValue,
        leadsToday: dayRows.find((day) => day.isToday)?.total ?? 0,
        avgPerDay,
        projectedMonthEnd: avgPerDay * getDaysInMonth(zonedNow),
        openCount: counts.Open,
        wonCount: counts.Won,
        lostCount: counts.Lost,
        wonValue: values.Won,
        conversionRate: totalLeads > 0 ? (counts.Won / totalLeads) * 100 : 0,
      });
      setDays(dayRows);
      setOutcomes(outcomeRows);
      setFeedDays(Array.from(feedByDay.values()));
    } catch (err) {
      console.error('Error loading CRM leads month details:', err);
      setSummary(EMPTY_SUMMARY);
      setDays([]);
      setOutcomes([]);
      setFeedDays([]);
      setError('Could not load lead details.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { summary, days, outcomes, feedDays, monthLabel, loading, error, load };
}
